import { useState, useEffect, useRef } from "react";

export interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isExpired: boolean;
}

/**
 * Simple countdown hook
 */
export const useCountdown = (
  targetDate: Date | string | number | undefined
): TimeLeft & { mounted: boolean } => {
  const [mounted, setMounted] = useState(false);
  const [days, setDays] = useState(0);
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [isExpired, setIsExpired] = useState(true);

  // Store target as ref to avoid dependency issues
  const targetRef = useRef<number>(0);

  // Update target ref when prop changes
  useEffect(() => {
    if (!targetDate) {
      targetRef.current = 0;
    } else if (typeof targetDate === "number") {
      targetRef.current = targetDate;
    } else {
      targetRef.current = new Date(targetDate).getTime();
    }
  }, [targetDate]);

  useEffect(() => {
    setMounted(true);

    const calculate = () => {
      const target = targetRef.current;
      if (!target) {
        setDays(0);
        setHours(0);
        setMinutes(0);
        setSeconds(0);
        setIsExpired(true);
        return;
      }

      const now = Date.now();
      const difference = target - now;

      if (difference <= 0) {
        setDays(0);
        setHours(0);
        setMinutes(0);
        setSeconds(0);
        setIsExpired(true);
        return;
      }

      setDays(Math.floor(difference / (1000 * 60 * 60 * 24)));
      setHours(Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)));
      setMinutes(Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)));
      setSeconds(Math.floor((difference % (1000 * 60)) / 1000));
      setIsExpired(false);
    };

    // Initial calculation
    calculate();

    // Update every second
    const timer = setInterval(calculate, 1000);

    return () => clearInterval(timer);
  }, []); // Empty deps - only run once on mount

  return { days, hours, minutes, seconds, isExpired, mounted };
};

/**
 * Check if a date has passed
 */
export const isDatePassed = (date: Date | string | number | undefined): boolean => {
  if (!date) return true;
  const timestamp = typeof date === "number" ? date : new Date(date).getTime();
  return Date.now() > timestamp;
};
